import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAgreements } from '../services/api';

const AgreementList = () => {
  const [agreements, setAgreements] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await getAgreements(search, statusFilter);

      if (Array.isArray(res?.data)) {
        setAgreements(res.data);
      } else if (Array.isArray(res?.data?.data)) {
        setAgreements(res.data.data);
      } else {
        setAgreements([]);
        setError('We could not read the agreements list from the server.');
      }
    } catch (err) {
      console.error('Fetch Error:', err);
      setAgreements([]);
      setError('Unable to load agreements right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements();
  }, [search, statusFilter]);

  const approvedCount = agreements.filter((item) => item.status === 'approved').length;
  const pendingCount = agreements.filter((item) => item.status === 'pending').length;
  const rejectedCount = agreements.filter((item) => item.status === 'rejected').length;

  return (
    <div className="agreements-page">
      <style>{`
        .agreements-page {
          --bg: linear-gradient(135deg, #fff8ef 0%, #f7fbff 50%, #eef7f4 100%);
          --surface: rgba(255, 255, 255, 0.92);
          --surface-strong: #ffffff;
          --text: #1f2937;
          --muted: #6b7280;
          --line: #e5e7eb;
          --shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
          --accent: #d97706;
          --accent-dark: #92400e;
          --accent-soft: #fff1db;
          --success-bg: #ecfdf3;
          --success-text: #047857;
          --warning-bg: #fff7ed;
          --warning-text: #c2410c;
          --danger-bg: #fef2f2;
          --danger-text: #b91c1c;
          min-height: 100vh;
          padding: 24px;
          background: var(--bg);
          color: var(--text);
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
        }

        .agreements-shell {
          max-width: 1200px;
          margin: 0 auto;
        }

        .hero-card,
        .filters-card,
        .table-card {
          background: var(--surface);
          border: 1px solid rgba(255, 255, 255, 0.7);
          box-shadow: var(--shadow);
          backdrop-filter: blur(10px);
          border-radius: 24px;
        }

        .hero-card {
          padding: 28px;
          margin-bottom: 20px;
          display: grid;
          gap: 18px;
        }

        .hero-top {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .hero-title {
          margin: 0;
          font-size: 2rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          margin: 8px 0 0;
          color: var(--muted);
          max-width: 720px;
          line-height: 1.6;
        }

        .hero-action {
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--accent) 0%, #f59e0b 100%);
          color: #fff;
          font-weight: 700;
          padding: 12px 18px;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 12px 24px rgba(217, 119, 6, 0.25);
        }

        .hero-action:hover {
          transform: translateY(-1px);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
        }

        .stat-card {
          background: var(--surface-strong);
          border: 1px solid var(--line);
          border-radius: 18px;
          padding: 16px;
        }

        .stat-label {
          margin: 0 0 8px;
          color: var(--muted);
          font-size: 0.92rem;
        }

        .stat-value {
          margin: 0;
          font-size: 1.7rem;
          font-weight: 800;
        }

        .filters-card {
          padding: 20px;
          margin-bottom: 20px;
        }

        .filters-row {
          display: grid;
          grid-template-columns: 2fr 1fr auto;
          gap: 14px;
          align-items: end;
        }

        .field-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 0.92rem;
          font-weight: 600;
        }

        .field {
          width: 100%;
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid var(--line);
          background: #fff;
          font-size: 0.98rem;
          color: var(--text);
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          box-sizing: border-box;
        }

        .field:focus {
          border-color: #f59e0b;
          box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.16);
        }

        .secondary-btn {
          height: 46px;
          padding: 0 16px;
          border-radius: 14px;
          border: 1px solid var(--line);
          background: #fff;
          color: var(--text);
          font-weight: 700;
          cursor: pointer;
        }

        .feedback-row {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
          margin-top: 14px;
        }

        .result-text {
          color: var(--muted);
          font-size: 0.95rem;
        }

        .error-banner {
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #b91c1c;
          padding: 12px 14px;
          border-radius: 14px;
          font-size: 0.95rem;
        }

        .table-card {
          overflow: hidden;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 20px 22px;
          border-bottom: 1px solid var(--line);
        }

        .table-title {
          margin: 0;
          font-size: 1.1rem;
        }

        .table-wrap {
          width: 100%;
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 760px;
        }

        th,
        td {
          padding: 16px 18px;
          text-align: left;
          border-bottom: 1px solid var(--line);
        }

        th {
          background: #fffaf3;
          color: var(--accent-dark);
          font-size: 0.86rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        tbody tr:hover {
          background: rgba(255, 248, 239, 0.65);
        }

        .name-cell {
          font-weight: 700;
        }

        .muted-cell {
          color: var(--muted);
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 96px;
          padding: 8px 12px;
          border-radius: 999px;
          font-size: 0.88rem;
          font-weight: 700;
        }

        .status-approved {
          background: var(--success-bg);
          color: var(--success-text);
        }

        .status-pending {
          background: var(--warning-bg);
          color: var(--warning-text);
        }

        .status-rejected {
          background: var(--danger-bg);
          color: var(--danger-text);
        }

        .view-btn {
          border: none;
          border-radius: 12px;
          background: #111827;
          color: #fff;
          padding: 10px 14px;
          font-weight: 700;
          cursor: pointer;
        }

        .loading-state,
        .empty-state {
          padding: 42px 20px;
          text-align: center;
          color: var(--muted);
        }

        .loading-state strong,
        .empty-state strong {
          display: block;
          color: var(--text);
          margin-bottom: 8px;
          font-size: 1.05rem;
        }

        @media (max-width: 900px) {
          .stats-grid,
          .filters-row {
            grid-template-columns: 1fr;
          }

          .hero-action,
          .secondary-btn {
            width: 100%;
          }
        }

        @media (max-width: 640px) {
          .agreements-page {
            padding: 16px;
          }

          .hero-card,
          .filters-card {
            padding: 18px;
          }

          .hero-title {
            font-size: 1.6rem;
          }
        }
      `}</style>

      <div className="agreements-shell">
        <section className="hero-card">
          <div className="hero-top">
            <div>
              <h2 className="hero-title">Agreements Dashboard</h2>
              <p className="hero-subtitle">
                Search, filter, and review franchisee agreements from one clean
                workspace. The layout is designed to make status tracking faster
                and easier for everyday use.
              </p>
            </div>

            <button className="hero-action" onClick={fetchAgreements}>
              Refresh List
            </button>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <p className="stat-label">Total Agreements</p>
              <p className="stat-value">{agreements.length}</p>
            </div>

            <div className="stat-card">
              <p className="stat-label">Approved</p>
              <p className="stat-value">{approvedCount}</p>
            </div>

            <div className="stat-card">
              <p className="stat-label">Pending</p>
              <p className="stat-value">{pendingCount}</p>
            </div>

            <div className="stat-card">
              <p className="stat-label">Rejected</p>
              <p className="stat-value">{rejectedCount}</p>
            </div>
          </div>
        </section>

        <section className="filters-card">
          <div className="filters-row">
            <div className="field-group">
              <label htmlFor="agreement-search">Search</label>
              <input
                id="agreement-search"
                className="field"
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="field-group">
              <label htmlFor="agreement-status">Status</label>
              <select
                id="agreement-status"
                className="field"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <button
              className="secondary-btn"
              onClick={() => {
                setSearch('');
                setStatusFilter('');
              }}
            >
              Clear Filters
            </button>
          </div>

          <div className="feedback-row">
            <span className="result-text">
              {loading ? 'Loading results...' : `${agreements.length} result(s) found`}
            </span>

            {error ? <div className="error-banner">{error}</div> : null}
          </div>
        </section>

        <section className="table-card">
          <div className="table-header">
            <h3 className="table-title">Agreement Records</h3>
            <span className="result-text">Click view to open agreement details</span>
          </div>

          {loading ? (
            <div className="loading-state">
              <strong>Loading agreements...</strong>
              Please wait while we fetch the latest records.
            </div>
          ) : agreements.length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {agreements.map((item) => (
                    <tr key={item.id}>
                      <td className="muted-cell">#{item.id}</td>
                      <td className="name-cell">{item.franchisee_name || '-'}</td>
                      <td className="muted-cell">{item.email || '-'}</td>
                      <td>{formatDate(item.agreement_date)}</td>
                      <td>
                        <span
                          className={`status-pill status-${item.status || 'pending'}`}
                        >
                          {getStatusLabel(item.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => navigate(`/agreement/${item.id}`)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <strong>No agreements found</strong>
              Try adjusting the search text or selecting a different status.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AgreementList;
